import { AnyAction, Middleware } from 'redux';
import type { AppClockService, DateTimeUtc } from '@mews-framework/datetime';
import { IgnoreError } from '@mews-framework/errors';
import { ActionWithPayload } from '@mews-framework/redux';
import { createHandler } from '@mews/std';

const noop = () => {};

export const WEBSOCKET_CONNECTED = '@@websocket/WEBSOCKET_CONNECTED';
export const WEBSOCKET_DISCONNECTED = '@@websocket/WEBSOCKET_DISCONNECTED';
export const WEBSOCKET_MESSAGE_SENT = '@@websocket/WEBSOCKET_MESSAGE_SENT';

export interface CreateWebsocketMiddleware {
    context?: Window;
    onOpen?: () => AnyAction;
    onClose?: (event: CloseEvent) => AnyAction;
    onError?: () => AnyAction;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onMessage?: (data: any) => AnyAction; // any comes from websocket type definitions
    handleExpiredSession?: () => AnyAction;
    ignoredErrorNames?: string[];
    isSessionExpired?: (state: unknown, props: { appNowUtc: DateTimeUtc }) => boolean;
    appClockService?: AppClockService;
}

type ContextWebSocket = WebSocket | null;

export const createWebsocketMiddleware =
    ({
        context = window,
        onOpen,
        onClose,
        onError,
        onMessage,
        ignoredErrorNames,
        isSessionExpired,
        handleExpiredSession,
        appClockService,
    }: CreateWebsocketMiddleware = {}): Middleware =>
    ({ dispatch, getState }) => {
        let websocket: ContextWebSocket = null;

        const isWebsocketOpen = () =>
            websocket !== null && (websocket as ContextWebSocket)?.readyState === context.WebSocket.OPEN;
        const messagesQueue: string[] = [];

        const closeConnection = () => {
            if (websocket !== null) {
                websocket.close();
                websocket = null;
                messagesQueue.splice(0, messagesQueue.length);
            }
        };

        const socketHandler = createHandler(
            {
                [WEBSOCKET_CONNECTED]: <T extends string, P extends string>(action: ActionWithPayload<T, P>) => {
                    if (
                        isSessionExpired &&
                        appClockService &&
                        isSessionExpired(getState(), { appNowUtc: appClockService.getAppNowUtc() }) &&
                        handleExpiredSession
                    ) {
                        dispatch(handleExpiredSession());
                    } else {
                        try {
                            closeConnection();

                            websocket = new context.WebSocket(action.payload);

                            websocket.onerror = () => {
                                if (onError) {
                                    dispatch(onError());
                                }
                            };

                            websocket.onopen = () => {
                                if (onOpen) {
                                    dispatch(onOpen());
                                }
                                messagesQueue.forEach((message) => websocket?.send(message));
                            };

                            websocket.onclose = (event) => {
                                if (onClose) {
                                    dispatch(onClose(event));
                                }
                                websocket = null;
                            };

                            websocket.onmessage = (event) => {
                                if (onMessage) {
                                    dispatch(onMessage(event.data));
                                }
                            };
                        } catch (error) {
                            if (error instanceof Error && ignoredErrorNames && ignoredErrorNames.includes(error.name)) {
                                throw new IgnoreError(error.message);
                            }
                            throw error;
                        }
                    }
                },

                [WEBSOCKET_MESSAGE_SENT]: <T extends string, P extends string>(action: ActionWithPayload<T, P>) => {
                    const message = JSON.stringify(action.payload);

                    if (!isWebsocketOpen()) {
                        return messagesQueue.push(message);
                    }

                    websocket?.send(message);
                },

                [WEBSOCKET_DISCONNECTED]: () => {
                    closeConnection();
                },
            },
            noop
        );

        return (next) => (action) => {
            socketHandler(action.type, action);
            return next(action);
        };
    };
